import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "styled-components/macro";
import styled from "styled-components";
import SkeletonScreen from "components/SkeletonScreen";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowDown, faArrowUp } from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import Pagination from "features/discoveries/components/table/Pagination";
import EmptyResults from "components/EmptyResults";
import Spinner from "components/SpinnerNew";
import useFetchAllEntities from "services/hooks/entities/useFetchAllEntities";
import IssuesSeverityLevel from "features/dashboard/widgets/issues.overview/SeverityLevel";
import InfoModal from "./InfoModal";

const StyledCount = styled.div`
  text-transform: uppercase;
  font-weight: bold;
  font-size: 2rem;
`;

const DateCell = ({ children }) => (
  <div>{children ? moment(children).format("DD/MM/YYYY") : "N/A"}</div>
);

const ScoreCell = ({ children }) => (
  <div>{children !== null ? children : "N/A"}</div>
);

const EntitiesTable = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [sorting, setSorting] = useState([
    { id: "lastCartographyDate", desc: true },
  ]);
  const [{ pageIndex, pageSize }, setPagination] = useState({
    pageIndex: 0, // Must be 0 instead of 1 because of react-table
    pageSize: 10,
  });

  const pagination = useMemo(
    () => ({
      pageIndex,
      pageSize,
    }),
    [pageIndex, pageSize]
  );

  const apiPage = pageIndex + 1;

  const { isSuccess, isLoading, isFetching, data } = useFetchAllEntities({
    page: apiPage,
    pageSize,
    sorting: sorting,
  });

  const columns = useMemo(
    () => [
      {
        accessorKey: "entityName",
        header: "Entity Name",
      },
      {
        accessorKey: "score",
        header: "Severity Score",
        cell: ({ getValue }) => <ScoreCell>{getValue()}</ScoreCell>,
      },
      {
        accessorKey: "allOpenIssues",
        header: "Open Issues",
      },
      {
        accessorKey: "allActiveAssets",
        header: "Active Assets",
      },
      {
        accessorKey: "lastCartographyDate",
        header: "Last Cartography Date",
        cell: ({ getValue }) => <DateCell>{getValue()}</DateCell>,
      },
    ],
    []
  );

  const table = useReactTable({
    data: data?.entities || [],
    columns,
    pageCount: Math.ceil((data?.totalEntities || 0) / pageSize),
    state: {
      pagination,
      sorting,
    },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
  });

  const onClickRow = (entityId) => {
    navigate(`/dashboard?entityId=${entityId}`);
  };

  // Get KPI values from response
  const totalEntities = data?.totalEntities || 0;
  const totalOpenIssues = data?.totalOpenIssues || 0;
  const totalAssets = data?.totalActiveAssets || 0;

  const globalHighestLevel = useMemo(() => {
    const score = data?.highestScore !== undefined ? data.highestScore : 5;
    const severityMap = {
      1: { value: "critical", label: "Critical" },
      2: { value: "high", label: "High" },
      3: { value: "medium", label: "Medium" },
      4: { value: "low", label: "Low" },
      5: { value: "info", label: "Info" },
    };
    return { ...severityMap[score], score };
  }, [data]);

  return (
    <div className="overflow-auto p-4">
      <h5 className="mb-4">Entities Overview</h5>
      <div className="row flex-sm-column flex-md-row g-2 mb-4">
        <div className="col-sm-12 col-md-3">
          <div className="d-flex flex-column surface h-100 p-2">
            <div className="title mt-3 d-flex justify-content-center align-items-center">
              HIGHEST SEVERITY SCORE
              <InfoModal />
            </div>

            <div className="d-flex flex-grow-1 justify-content-center align-items-center align-self-center">
              {isLoading || isFetching ? (
                <Spinner color={theme.primary} />
              ) : (
                <IssuesSeverityLevel highestLevel={globalHighestLevel} />
              )}
            </div>
          </div>
        </div>
        <div className="col-sm-12 col-md-3">
          <div className="d-flex flex-column surface h-100 p-2">
            <div className="title text-center mt-3">TOTAL OPEN ISSUES</div>
            <div className="d-flex flex-grow-1 justify-content-center align-items-center align-self-center">
              {isLoading || isFetching ? (
                <Spinner color={theme.primary} />
              ) : (
                <StyledCount>{totalOpenIssues}</StyledCount>
              )}
            </div>
          </div>
        </div>
        <div className="col-sm-12 col-md-3">
          <div className="d-flex flex-column surface h-100 p-2">
            <div className="title text-center mt-3">TOTAL ASSETS</div>
            <div className="d-flex flex-grow-1 justify-content-center align-items-center align-self-center">
              {isLoading || isFetching ? (
                <Spinner color={theme.primary} />
              ) : (
                <StyledCount>{totalAssets}</StyledCount>
              )}
            </div>
          </div>
        </div>
        <div className="col-sm-12 col-md-3">
          <div className="d-flex flex-column surface h-100 p-2">
            <div className="title text-center mt-3">TOTAL ENTITIES</div>
            <div
              style={{ height: 100 }}
              className="d-flex flex-grow-1 justify-content-center align-items-center align-self-center"
            >
              {isLoading || isFetching ? (
                <Spinner color={theme.primary} />
              ) : (
                <StyledCount>{totalEntities}</StyledCount>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className="surface h-100 p-2">
        <div className="d-flex justify-content-end mb-3">
          <Pagination table={table} isFetching={isFetching} />
        </div>

        <div className="table-responsive">
          {isLoading || isFetching ? (
            <SkeletonScreen width="100%" height={41} count={10} />
          ) : (
            <table className="table">
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th key={header.id} colSpan={header.colSpan}>
                        {!header.isPlaceholder && (
                          <div
                            className={
                              header.column.getCanSort()
                                ? "cursor-pointer select-none"
                                : ""
                            }
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                            {{
                              asc: (
                                <FontAwesomeIcon
                                  icon={faArrowDown}
                                  size="1x"
                                  className="ms-2"
                                />
                              ),
                              desc: (
                                <FontAwesomeIcon
                                  icon={faArrowUp}
                                  size="1x"
                                  className="ms-2"
                                />
                              ),
                            }[header.column.getIsSorted()] ?? null}
                          </div>
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr
                    role="button"
                    key={row.id}
                    onClick={() => onClickRow(row.original.entityId)}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {isSuccess && !isLoading && !isFetching && !data?.entities.length && (
            <EmptyResults className="my-5">No entities found</EmptyResults>
          )}
        </div>
      </div>
    </div>
  );
};

export default EntitiesTable;
